import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditPetProfileForm, EditPetProfileSchema, Input } from '$features/pets/forms/edit-pet-profile/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, petUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { useFileUpload } from '$hooks/use-file-upload'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditPetProfileModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const fileHandler = useFileUpload()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updatePet, isPending: isUpdatingPet } = useMutation({
    ...petUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
          query: {
            [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
          },
        }),
      })
      ToastHelper.success('Pet profile updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update pet profile.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditPetProfileSchema) => {
    updatePet({
      path: {
        pet: Number(id),
      },
      body: {
        name: values[Input.Name],
        media_id: fileHandler.getFile(Input.Image)?.key,
      },
    })
  }

  const pet = data?.data

  if (!pet) {
    return null
  }

  return (
    <EditPetProfileForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingPet}
      fileHandler={fileHandler}
      defaultValues={{
        [Input.Name]: pet.name,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Pet Profile
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>

          <EditPetProfileForm.Content />
        </NavigableModal.Content>
        <NavigableModal.Footer>
          <EditPetProfileForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditPetProfileForm>
  )
}
